/**
 * PaymentDashboard - Payment Analytics Dashboard
 * Migrated from src/app/components/admin/PaymentDashboard.jsx
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import AdminCache from '../components/shared/AdminCache';
import SkeletonLoaderComponents from '../components/shared/SkeletonLoader';
import { API_ENDPOINTS } from '../config/api-endpoints';

const { DashboardSkeleton } = SkeletonLoaderComponents;

const PaymentDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('monthly');

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${API_ENDPOINTS.ADMIN.PAYMENT_ANALYTICS}/metrics?period=${period}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Payment analytics fetch failed: ${response.status}`);
      }
      const data = await response.json();
      setMetrics(data.data?.metrics || data.metrics || data || {});
    } catch (err) {
      console.error('[PaymentDashboard] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading && !metrics) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="payment-dashboard">
      <div className="payment-dashboard__header">
        <h2>Payment Analytics</h2>
        <div className="payment-dashboard__period-selector">
          {['daily', 'weekly', 'monthly', 'yearly'].map((p) => (
            <button
              key={p}
              className={`payment-dashboard__period-btn ${period === p ? 'payment-dashboard__period-btn--active' : ''}`}
              onClick={() => setPeriod(p)}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="admin-alert admin-alert--warning">
          <span>⚠️</span> {error}
        </div>
      )}

      <div className="admin-grid admin-grid-4">
        <div className="admin-stat-card">
          <div className="admin-stat-icon green">💰</div>
          <div className="admin-stat-value">{formatCurrency(metrics?.totalRevenue)}</div>
          <div className="admin-stat-label">Total Revenue</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon blue">📊</div>
          <div className="admin-stat-value">{metrics?.totalTransactions ?? '—'}</div>
          <div className="admin-stat-label">Total Transactions</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon purple">✅</div>
          <div className="admin-stat-value">{metrics?.successRate != null ? `${metrics.successRate.toFixed(1)}%` : '—'}</div>
          <div className="admin-stat-label">Success Rate</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon orange">💵</div>
          <div className="admin-stat-value">{formatCurrency(metrics?.averageTransactionValue)}</div>
          <div className="admin-stat-label">Avg Transaction</div>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Transaction Breakdown</h3>
        </div>
        <div className="payment-dashboard__breakdown">
          <div className="payment-dashboard__breakdown-item payment-dashboard__breakdown-item--success">
            <span>✅ Successful</span>
            <span>{metrics?.successfulTransactions ?? '—'}</span>
          </div>
          <div className="payment-dashboard__breakdown-item payment-dashboard__breakdown-item--failed">
            <span>❌ Failed</span>
            <span>{metrics?.failedTransactions ?? '—'}</span>
          </div>
          <div className="payment-dashboard__breakdown-item payment-dashboard__breakdown-item--pending">
            <span>⏳ Pending</span>
            <span>{metrics?.pendingTransactions ?? '—'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDashboard;
